/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils\extensionMapper.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
────────────────────────────────────────────────────────────────*/


import { v4 as uuidv4 } from 'uuid';
import { getSettingValue } from './settingsHelpers';
import { parseLanguagePayload, buildSoundPrefixPath, getDefaultVoicemailLanguage } from './languageHelpers';
import { DEFAULT_LANGUAGE_CODE } from '../../../data/formOptions';
import { generateDefaultPassword, generateDefaultSecret } from './passwordGenerators';
import type {
    ExtensionFormState,
    VoicemailSettingsState,
    RecordingSettingsState,
    ContactInfoState,
    FollowMeSettingsState,
    PersonalAssistantState,
} from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RawExtension = Record<string, any>;

// ── Time-group ID parser ──────────────────────────────────────
function parseTimeGroupId(value: string): number | null {
    if (!value || value.trim() === '') return null;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
}

// ── Form data mapper ──────────────────────────────────────────
export function mapExtensionToFormData(ext: RawExtension, duplicate = false): ExtensionFormState {
    const { languageCode: normalizedLanguageCode, voice: normalizedVoice } = parseLanguagePayload(ext.language_code);
    const existingSoundPrefix = getSettingValue(ext.settings, 'sound_prefix') || '';
    const soundPrefix = existingSoundPrefix && !existingSoundPrefix.includes('${') && !existingSoundPrefix.includes('$$')
        ? existingSoundPrefix
        : buildSoundPrefixPath(normalizedLanguageCode, normalizedVoice);

    const mapped: ExtensionFormState = {
        extension: ext.extension || '',
        name: ext.name || getSettingValue(ext.settings, 'name') || '',
        extension_type: ext.extension_type || 'sip',
        languageCode: normalizedVoice ? `${normalizedLanguageCode}-${normalizedVoice}` : normalizedLanguageCode,
        voice: normalizedVoice,
        soundPrefix,
        enabled: ext.enabled ?? true,
        classOfServicesId: ext.class_of_services_id || null,
        dialProfileId: ext.dial_profile_id ?? ext.dialProfileId ?? null,
        callCenterAgentProfileId: ext.call_center_agent_profile_id || null,
        featuresPassword: getSettingValue(ext.settings, 'features_password') || ext.features_password || '',
        emailAddress: getSettingValue(ext.settings, 'vm-email-all-messages'),
        billingEnabled: ext.billing_enabled || false,
        voicemailEnabled: ext.voicemail_enabled || false,
        blfSupervisionEnabled: ext.blf_supervision_enabled || false,
        internalCidName: getSettingValue(ext.settings, 'effective_caller_id_name'),
        internalCidNumber: getSettingValue(ext.settings, 'effective_caller_id_number'),
        externalCidName: getSettingValue(ext.settings, 'outbound_caller_id_name'),
        externalCidNumber: getSettingValue(ext.settings, 'outbound_caller_id_number'),
        emergencyCidName: getSettingValue(ext.settings, 'emergency_caller_id_name'),
        emergencyCidNumber: getSettingValue(ext.settings, 'emergency_caller_id_number'),
        accountCode: getSettingValue(ext.settings, 'accountcode'),
        tollAllow: getSettingValue(ext.settings, 'toll_allow') || 'domestic,international,local',
        callTimeout: ext.ring_timeout ?? (parseInt(getSettingValue(ext.settings, 'call_timeout')) || 30),
        maxCalls: ext.maxCalls ?? 0,
        callWaiting: getSettingValue(ext.settings, 'call_waiting') === 'true',
        rejectAnonymous: getSettingValue(ext.settings, 'reject_anonymous') === 'true',
        autoAnswer: getSettingValue(ext.settings, 'auto_answer') === 'true',
        holdMusic: getSettingValue(ext.settings, 'music_on_hold') || getSettingValue(ext.settings, 'hold_music') || '',
        privacy: getSettingValue(ext.settings, 'privacy') || 'none',
        callScreen: getSettingValue(ext.settings, 'call_screen') === 'true',
        presenceId: getSettingValue(ext.settings, 'presence_id') || '',
        ringtone: getSettingValue(ext.settings, 'ringtone') || '',
        effectiveCallerIdName: getSettingValue(ext.settings, 'effective_caller_id_name') || '',
        effectiveCallerIdNumber: getSettingValue(ext.settings, 'effective_caller_id_number') || '',
        pinless: getSettingValue(ext.settings, 'pinless') === 'true',
        forwardTo: ext.virtual_forward_to || getSettingValue(ext.settings, 'forward_to') || '',
        portalEnabled: ext.portal_enabled || false,
        portalUser: ext.portal_user || '',
        portalPassword: ext.portal_password || '',
        softphoneEnabled: ext.softphone_enabled || false,
        deviceUsername: '',
        devicePassword: '',
        deviceDescription: '',
        deviceSipProfileId: null,
        deviceDispatchLocationId: null,
        deviceMaxContacts: '5',
        deviceOutboundCodecs: 'PCMU,PCMA',
        deviceInboundCodecs: 'PCMU,PCMA',
        deviceDtmfMode: 'rfc4733',
        smsNumberId: ext.smsNumberId ?? ext.sms_number_id ?? null,
        faxEnabled: ext.faxEnabled ?? ext.fax_enabled ?? false,
        faxNumber: ext.faxNumber ?? ext.fax_number ?? '',
        faxEmailTo: ext.faxEmailTo ?? ext.fax_email_to ?? '',
        faxEmailFrom: ext.faxEmailFrom ?? ext.fax_email_from ?? '',
        faxDeviceId: ext.faxDeviceId ?? ext.fax_device_id ?? null,
        chatbotEnabled: ext.chatbotEnabled ?? ext.chatbot_enabled ?? false,
        chatbotAgentId: ext.chatbotAgentId ?? ext.chatbot_agent_id ?? null,
        simultaneousTranslationEnabled: ext.simultaneousTranslationEnabled ?? ext.simultaneous_translation_enabled ?? false,
        aiTranslatorProfileId: ext.aiTranslatorProfileId ?? ext.ai_translator_profile_id ?? null,
    };

    if (duplicate) {
        mapped.extension = '';
        mapped.name = mapped.name ? `${mapped.name} Copy`.trim() : mapped.name;
        mapped.featuresPassword = generateDefaultPassword();
        mapped.deviceUsername = '';
        mapped.devicePassword = generateDefaultSecret();
    }

    return mapped;
}

export function mapExtensionToDeviceData(ext: RawExtension): Partial<ExtensionFormState> {
    if (ext.extension_type !== 'sip') return {};
    const firstDevice = ext.devices && ext.devices.length > 0 ? ext.devices[0] : null;
    return {
        deviceUsername: firstDevice?.username || getSettingValue(ext.settings, 'user_id') || ext.extension,
        devicePassword: firstDevice?.password || getSettingValue(ext.settings, 'password') || 'r2a2025',
        deviceDescription: firstDevice?.description || ext.name,
        deviceSipProfileId: firstDevice?.sip_profile_id || null,
        deviceDispatchLocationId: firstDevice?.dispatch_location_id ?? null,
        deviceMaxContacts: getSettingValue(ext.settings, 'max-registrations') || '5',
        deviceOutboundCodecs: getSettingValue(ext.settings, 'outbound-codec-prefs') || 'PCMU,PCMA',
        deviceInboundCodecs: getSettingValue(ext.settings, 'inbound-codec-prefs') || 'PCMU,PCMA',
        deviceDtmfMode: getSettingValue(ext.settings, 'dtmf-type') || 'rfc4733',
    };
}

export function mapExtensionToVoicemail(ext: RawExtension, normalizedLanguageCode: string): VoicemailSettingsState | null {
    if (!ext.voicemail) return null;
    const voicemailLanguage = ext.voicemail.language || normalizedLanguageCode.split('-')[0] || getDefaultVoicemailLanguage(DEFAULT_LANGUAGE_CODE);
    return {
        password: ext.voicemail.password || '',
        email: ext.voicemail.email || '',
        maxMessages: ext.voicemail.max_messages || 100,
        notificationEnabled: ext.voicemail.notification_enabled ?? true,
        transcribeEnabled: ext.voicemail.transcribe_enabled ?? false,
        emailAttachment: ext.voicemail.email_attachment ?? true,
        language: voicemailLanguage,
        timezone: ext.voicemail.timezone || 'America/Managua',
        greeting: ext.voicemail.greeting || '',
        storagePath: ext.voicemail.storage_path || '',
        emailTemplate: ext.voicemail.email_template || '',
    };
}

export function mapExtensionToRecordingSettings(ext: RawExtension): RecordingSettingsState {
    return {
        recordIncoming: getSettingValue(ext.settings, 'record_incoming') === 'true',
        recordOutgoing: getSettingValue(ext.settings, 'record_outgoing') === 'true',
        recordInternal: getSettingValue(ext.settings, 'record_internal') === 'true',
        recordOnDemand: getSettingValue(ext.settings, 'record_on_demand') === 'true',
        recordingFormat: getSettingValue(ext.settings, 'recording_format') || 'wav',
        recordPath: getSettingValue(ext.settings, 'record_path') || '/recordings',
        recordTemplate: getSettingValue(ext.settings, 'recording_filename_template') || '${caller_id_number}-${uuid}.wav',
    };
}

export function mapExtensionToContactInfo(ext: RawExtension): ContactInfoState {
    return {
        name: getSettingValue(ext.settings, 'contact_name_given'),
        email: getSettingValue(ext.settings, 'contact_email'),
        phone: getSettingValue(ext.settings, 'contact_tel'),
        notes: getSettingValue(ext.settings, 'contact_note'),
    };
}

export function mapExtensionToFollowMe(ext: RawExtension): FollowMeSettingsState {
    const followMeEnabled = getSettingValue(ext.settings, 'follow_me_enabled') === 'true';
    const followMeDestinationsJson = getSettingValue(ext.settings, 'follow_me_destinations');
    let followMeDestinations: Array<{ id: string; destination: string; timeout: number; delay: number; prompt: string }> = [];

    if (followMeDestinationsJson) {
        try {
            const parsed = JSON.parse(followMeDestinationsJson);
            if (Array.isArray(parsed)) {
                followMeDestinations = parsed.map((dest) => ({
                    id: dest.id || uuidv4(),
                    destination: dest.destination || '',
                    timeout: dest.timeout || 30,
                    delay: dest.delay || 0,
                    prompt: dest.prompt || '',
                }));
            }
        } catch (e) {
            console.warn('Failed to parse follow_me_destinations JSON:', e);
        }
    }

    if (followMeDestinations.length === 0) {
        const followMeNumber = getSettingValue(ext.settings, 'follow_me_number') || '';
        if (followMeNumber) {
            followMeDestinations = [{
                id: uuidv4(),
                destination: followMeNumber,
                timeout: parseInt(getSettingValue(ext.settings, 'follow_me_timeout') || '30'),
                delay: parseInt(getSettingValue(ext.settings, 'follow_me_delay') || '0'),
                prompt: getSettingValue(ext.settings, 'follow_me_prompt') || '',
            }];
        }
    }

    return {
        callForwardEnabled: getSettingValue(ext.settings, 'call_forward_enabled') === 'true',
        callForwardDestination: getSettingValue(ext.settings, 'call_forward_destination') || '',
        callForwardTimeGroupId: parseTimeGroupId(getSettingValue(ext.settings, 'call_forward_time_group_id')),
        onBusyEnabled: getSettingValue(ext.settings, 'on_busy_enabled') === 'true',
        onBusyDestination: getSettingValue(ext.settings, 'on_busy_destination') || '',
        onBusyTimeGroupId: parseTimeGroupId(getSettingValue(ext.settings, 'on_busy_time_group_id')),
        noAnswerEnabled: getSettingValue(ext.settings, 'no_answer_enabled') === 'true',
        noAnswerDestination: getSettingValue(ext.settings, 'no_answer_destination') || '',
        noAnswerTimeGroupId: parseTimeGroupId(getSettingValue(ext.settings, 'no_answer_time_group_id')),
        notRegisteredEnabled: getSettingValue(ext.settings, 'not_registered_enabled') === 'true',
        notRegisteredDestination: getSettingValue(ext.settings, 'not_registered_destination') || '',
        notRegisteredTimeGroupId: parseTimeGroupId(getSettingValue(ext.settings, 'not_registered_time_group_id')),
        enabled: followMeEnabled,
        destinations: followMeDestinations,
        ringStrategy: (getSettingValue(ext.settings, 'follow_me_strategy') || 'simultaneous') as 'simultaneous' | 'sequential',
        skipBusy: getSettingValue(ext.settings, 'follow_me_skip_busy') === 'true',
        ignoreBusy: getSettingValue(ext.settings, 'follow_me_ignore_busy') === 'true',
        doNotDisturb: getSettingValue(ext.settings, 'do_not_disturb') === 'true',
        doNotDisturbTimeGroupId: parseTimeGroupId(getSettingValue(ext.settings, 'do_not_disturb_time_group_id')),
    };
}

export function mapExtensionToPersonalAssistant(ext: RawExtension): PersonalAssistantState {
    const paSettings = ext.personal_assistant_settings || {};
    return {
        enabled: ext.personal_assistant_enabled || false,
        instructionMessageRecordingId: ext.instruction_message_recording_id ?? paSettings.instructionMessageRecordingId ?? null,
        options: Array.isArray(paSettings.options)
            ? paSettings.options.map((opt: { id?: string; digit?: string; action?: string; destination?: string; timeout?: number }) => ({
                id: opt.id || uuidv4(),
                digit: opt.digit || '1',
                action: opt.action || 'extension',
                destination: opt.destination || '',
                timeout: opt.timeout || 30,
            }))
            : [],
        timeout: paSettings.timeout || 10,
        maxRetries: paSettings.maxRetries || 3,
        fallbackAction: paSettings.fallbackAction || 'voicemail',
    };
}
